import type { DatasetState } from '@/lib/dashRuntime';
import type { DashboardDataset } from '@/lib/dashboards-api';
import { cn } from '@/lib/utils';
import {
    ArrowLeft,
    ChevronRight,
    Copy,
    Database,
    LoaderCircle,
    RefreshCw,
    TriangleAlert,
    X
} from 'lucide-react';
import { useState } from 'react';

interface DashboardSourcesProps {
    datasets: DashboardDataset[];
    states: Record<string, DatasetState>;
    onRefresh: (datasetId: string) => void;
    onEdit: (dataset: DashboardDataset) => void;
    onClose: () => void;
}

const COLLAPSED_ARGUMENT_CHARS = 280;

const dotClass: Record<string, string> = {
    ok: 'bg-green-600',
    error: 'bg-red-600',
    missing: 'bg-red-600'
};

const argumentText = (value: unknown) =>
    typeof value === 'string' ? value : JSON.stringify(value, null, 2);

function StatusMark({
    status,
    refreshing
}: {
    status?: string;
    refreshing?: boolean;
}) {
    if (!status || status === 'loading' || refreshing) {
        return (
            <LoaderCircle className="w-3.5 h-3.5 text-muted-foreground animate-slow-spin" />
        );
    }
    return (
        <span
            className={cn('rounded-full w-2 h-2', dotClass[status])}
            aria-label={status}
        />
    );
}

function ArgumentBlock({ name, value }: { name: string; value: unknown }) {
    const [expanded, setExpanded] = useState(false);
    const text = argumentText(value);
    const isLong = text.length > COLLAPSED_ARGUMENT_CHARS;
    const shown =
        isLong && !expanded
            ? `${text.slice(0, COLLAPSED_ARGUMENT_CHARS)}…`
            : text;

    return (
        <div className="flex flex-col gap-1">
            <div className="flex justify-between items-center text-muted-foreground text-xs">
                <span>{name}</span>
                <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(text)}
                    aria-label={`Copy ${name}`}
                    className="hover:text-foreground"
                >
                    <Copy className="w-3.5 h-3.5" />
                </button>
            </div>
            <pre className="bg-muted p-2 rounded overflow-auto font-mono text-xs break-words whitespace-pre-wrap">
                {shown}
            </pre>
            {isLong && (
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    className="self-start text-muted-foreground hover:text-foreground text-xs"
                >
                    {expanded ? 'Show less' : 'Show more'}
                </button>
            )}
        </div>
    );
}

function SourceDetail({
    dataset,
    state,
    onBack,
    onRefresh,
    onEdit,
    onClose
}: {
    dataset: DashboardDataset;
    state?: DatasetState;
    onBack: () => void;
    onRefresh: () => void;
    onEdit: () => void;
    onClose: () => void;
}) {
    const rowCount = Array.isArray(state?.data) ? state.data.length : null;
    const args = Object.entries(dataset.args ?? {});

    return (
        <>
            <div className="flex justify-between items-center px-3 py-2 text-muted-foreground">
                <button
                    type="button"
                    onClick={onBack}
                    aria-label="Back to sources"
                    className="hover:text-foreground"
                >
                    <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={onRefresh}
                        aria-label="Refresh this source"
                        className="hover:text-foreground"
                    >
                        <RefreshCw
                            className={cn(
                                'w-4 h-4',
                                state?.status === 'loading' &&
                                    'animate-slow-spin'
                            )}
                        />
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close sources"
                        className="hover:text-foreground"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
            <div className="flex flex-col flex-1 gap-3 px-3 pb-4 overflow-y-auto">
                <div className="flex flex-col gap-1">
                    <h3 className="font-medium text-sm">{dataset.title}</h3>
                    {dataset.description && (
                        <p className="text-muted-foreground text-sm">
                            {dataset.description}
                        </p>
                    )}
                </div>
                {state?.status === 'error' && (
                    <div className="flex gap-2 bg-destructive/10 p-2 rounded text-destructive text-sm">
                        <TriangleAlert className="mt-0.5 w-4 h-4 shrink-0" />
                        <div className="flex flex-col gap-1">
                            {state.accessDenied && (
                                <span className="font-medium">
                                    You probably do not have access to this
                                    data.
                                </span>
                            )}
                            <span className="break-words">
                                {state.message || 'Could not load this source.'}
                            </span>
                        </div>
                    </div>
                )}
                {rowCount !== null && state?.status === 'ok' && (
                    <p className="text-muted-foreground text-xs">
                        {rowCount} {rowCount === 1 ? 'row' : 'rows'} loaded
                    </p>
                )}
                {args.length > 0 && (
                    <section className="flex flex-col gap-3">
                        <h4 className="font-medium text-sm">Query</h4>
                        {args.map(([name, value]) => (
                            <ArgumentBlock
                                key={name}
                                name={name}
                                value={value}
                            />
                        ))}
                    </section>
                )}
                <button
                    type="button"
                    onClick={onEdit}
                    className="self-start hover:bg-muted px-3 py-1.5 border border-border rounded-lg text-sm"
                >
                    Edit source
                </button>
                <p className="text-muted-foreground text-xs">
                    {dataset.connector} -{' '}
                    <span className="font-mono">{dataset.tool}</span>
                </p>
            </div>
        </>
    );
}

export function DashboardSources({
    datasets,
    states,
    onRefresh,
    onEdit,
    onClose
}: DashboardSourcesProps) {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const selected = datasets.find((dataset) => dataset.id === selectedId);

    return (
        <aside className="flex flex-col border-border border-l w-72 min-h-0 shrink-0">
            {selected ? (
                <SourceDetail
                    dataset={selected}
                    state={states[selected.id]}
                    onBack={() => setSelectedId(null)}
                    onRefresh={() => onRefresh(selected.id)}
                    onEdit={() => onEdit(selected)}
                    onClose={onClose}
                />
            ) : (
                <>
                    <div className="flex justify-between items-center px-3 py-2">
                        <span className="font-medium text-sm">Sources</span>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close sources"
                            className="text-muted-foreground hover:text-foreground"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <ul className="flex-1 px-1 overflow-y-auto">
                        {datasets.map((dataset) => (
                            <li key={dataset.id}>
                                <button
                                    type="button"
                                    onClick={() => setSelectedId(dataset.id)}
                                    className="flex items-center gap-2 hover:bg-muted px-2 py-2 rounded w-full text-sm text-left"
                                >
                                    <span className="flex-1 truncate">
                                        {dataset.title}
                                    </span>
                                    <StatusMark
                                        status={states[dataset.id]?.status}
                                        refreshing={
                                            states[dataset.id]?.refreshing
                                        }
                                    />
                                    <Database className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                </button>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </aside>
    );
}
